import { type ChangeEvent, forwardRef, useRef } from "react";
import TextField, { type OutlinedTextFieldProps, type TextFieldProps } from "@mui/material/TextField";
import { InputAdornment } from "@mui/material";
import IconComponent from "@CubeComponents/icons";

type CubeTextFieldProps = Omit<OutlinedTextFieldProps, "variant"> & {
  variant?: "outlined" | "filled" | "standard";
  showClearButton?: boolean;
  height?: string;
  width?: string;
};

const TextFieldComponent = forwardRef<HTMLInputElement, CubeTextFieldProps>((props, ref) => {
  const internalRef = useRef<HTMLInputElement>(null);
  const { height = "32px", sx = {}, multiline = false, showClearButton, ...restProps } = props;

  const setRefs = (element: HTMLInputElement | null) => {
    internalRef.current = element;
    if (typeof ref === "function") {
      ref(element);
    } else if (ref) {
      (ref as React.MutableRefObject<HTMLInputElement | null>).current = element;
    }
  };

  const slotProps: TextFieldProps["slotProps"] = { ...restProps.slotProps };
  if (showClearButton && !(slotProps?.input as Record<string, unknown>)?.endAdornment) {
    slotProps!.input = {
      ...slotProps?.input,
      endAdornment: (
        <InputAdornment position="end">
          <IconComponent
            name="cross"
            onClick={() => {
              if (internalRef.current) {
                internalRef.current.value = "";
                restProps.onChange?.({ target: internalRef.current } as ChangeEvent<HTMLInputElement>);
              }
            }}
            width="1.125rem"
            height="1.125rem"
            className="cursor-pointer"
          />
        </InputAdornment>
      ),
    };
  }

  return (
    <TextField
      inputRef={setRefs}
      multiline={multiline}
      sx={{
        width: props.width ?? "100%",
        fontSize: "0.875rem",
        fontStyle: "normal",
        fontWeight: 400,
        lineHeight: "normal",
        ...sx,
        "& .MuiOutlinedInput-root": {
          backgroundColor: "var(--surface-white)",
          ...sx?.["& .MuiOutlinedInput-root"],
          minHeight: height,
          borderColor: "#666A84",
          paddingTop: "0 !important",
          paddingInlineStart: "0 !important",
          paddingBottom: "0 !important",
        },
        "& .MuiInputBase-input::placeholder": {
          color: "#666A84",
          opacity: 1,
        },

        "& .MuiOutlinedInput-root:hover .MuiInputBase-input::placeholder": {
          color: "#192048 !important",
        },
        "& .MuiOutlinedInput-notchedOutline": {
          borderColor: "#666A84",
        },
        "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
          borderColor: "#6BBBE3",
          borderWidth: "1px",
          ...props.sx?.["& .MuiOutlinedInput-root"]?.["&.Mui-focused fieldset"],
        },
        "& .MuiOutlinedInput-root.Mui-disabled": {
          backgroundColor: "var(--light-blue-tag)",
        },
        "& .MuiOutlinedInput-root .MuiOutlinedInput-input": {
          backgroundColor: "var(--surface-white)",
          padding: "0.3125rem 0.625rem !important",
        },
        "& .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-input": {
          backgroundColor: "inherit",
        },
        "& .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline": {
          border: "none !important",
        },
      }}
      {...restProps}
      slotProps={slotProps}
    />
  );
});

TextFieldComponent.displayName = "TextFieldComponent";

export default TextFieldComponent;
